import type { ApprovalRef } from '../../generated/executionApprovals'
import type { DirectorSkillSelection } from '../chat/directorSkillSelection'
import { memo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { ReactFlowCanvasEdge, ReactFlowCanvasNode } from '../../hooks/useYCanvas'
import type { CanvasSummary } from '../../api/client'
import { ChevronLeft, ChevronsLeft, ChevronsRight, Terminal } from 'lucide-react'
import { AgentChat } from './AgentChat'
import { WORKSPACE_TOP_BAR_HEIGHT_CLASS } from './headerHeight'
import { useChatSidebarResize } from '../../hooks/useChatSidebarResize'
import {
  DEFAULT_AUTO_CONFIRM_COST_SETTINGS,
  type AutoConfirmCostSettings,
} from '../chat/autoConfirmCost'
import {
  WorkspaceSidebar,
  SidebarHeader,
  SidebarContent,
  ResizeHandle,
} from './WorkspaceShell'
import type { ClassifiedMediaFile } from '../../utils/uploadFileValidation'

interface ChatSidebarProps {
  projectId: string
  canvasId?: string
  initialPrompt?: string
  initialSelectedSkill?: DirectorSkillSelection
  initialReferencedNodeIds?: string[]
  pendingActions?: Array<{ tool: string; args: Record<string, string> }>
  initialReplayScenario?: string
  autoConfirmCostSettings?: AutoConfirmCostSettings
  /** Canvas nodes for @mention autocomplete */
  canvasNodes?: ReactFlowCanvasNode[]
  /** Live canvas nodes for replay operation checks that validate output fields */
  replayCanvasNodes?: ReactFlowCanvasNode[]
  /** Canvas edges for replay operation integrity checks */
  canvasEdges?: ReactFlowCanvasEdge[]
  /** Replay-only canvas create operation used by in-app operation cards */
  onReplayCreateCanvas?: (title: string) => Promise<{ id: string; title: string }>
  /** Replay-only canvas delete operation used by in-app operation cards */
  onReplayDeleteCanvas?: (canvasId: string) => Promise<void>
  /** Live Director-created canvas event from the chat websocket */
  onCanvasCreated?: (canvas: CanvasSummary) => void
  /** Fires on every chat WS connect/disconnect transition — see AgentChat prop docs. */
  onChatSocketConnectedChange?: (connected: boolean) => void
  /** Running non-director node count from YDoc (includes canvas-hidden nodes) */
  ydocRunningCount?: number
  /** Queued (pending_execution) non-director node count from YDoc */
  ydocQueuedCount?: number
  /** True when the director node's own YDoc status is `running` */
  ydocDirectorRunning?: boolean
  /** True when the YDoc-derived Director status is live rather than stale */
  ydocStatusLive?: boolean
  /** Called when a mention pill is clicked — focuses the canvas on that node */
  onMentionClick?: (nodeId: string) => void
  /** Called with a merged "Upload media" selection from Director chat */
  onUploadMedia?: (accepted: ClassifiedMediaFile[], rejected: File[]) => void
  /** Execution plan batches from YDoc */
  executionPlanBatches?: import('../../hooks/useExecutionPlan').ExecutionPlanBatch[]
  /** Confirm an execution plan batch */
  onConfirmBatch?: (quotes: ApprovalRef[]) => void | Promise<void>
  /** Cancel an execution plan batch */
  onCancelBatch?: (batchId: string) => void
  /** Mobile workspace presents chat/canvas as full-screen panes. */
  mobileMode?: boolean
  /** Switch to the canvas pane in mobile workspace mode. */
  onShowCanvas?: () => void
}

/**
 * Collapsible and resizable chat sidebar for the workspace.
 * Contains the AgentChat component for agentic workflow interactions.
 */
export const ChatSidebar = memo(function ChatSidebar({
  projectId,
  canvasId = 'agent',
  initialPrompt,
  initialSelectedSkill,
  initialReferencedNodeIds,
  pendingActions,
  initialReplayScenario,
  autoConfirmCostSettings = DEFAULT_AUTO_CONFIRM_COST_SETTINGS,
  canvasNodes,
  replayCanvasNodes,
  canvasEdges,
  onReplayCreateCanvas,
  onReplayDeleteCanvas,
  onCanvasCreated,
  onChatSocketConnectedChange,
  ydocRunningCount,
  ydocQueuedCount,
  ydocDirectorRunning,
  ydocStatusLive,
  onMentionClick,
  onUploadMedia,
  executionPlanBatches,
  onConfirmBatch,
  onCancelBatch,
  mobileMode = false,
  onShowCanvas,
}: ChatSidebarProps) {
  const [chatCollapsed, setChatCollapsed] = useState(() => {
    try { return localStorage.getItem('chat-sidebar-collapsed') === 'true' } catch { return false }
  })
  const { width: chatWidth, onPointerDown: handleResizePointerDown } = useChatSidebarResize(mobileMode)
  const { t } = useTranslation('workspace')
  const effectiveChatCollapsed = mobileMode ? false : chatCollapsed

  // Open chat sidebar when a node's "Add to Chat" toolbar button is clicked
  useEffect(() => {
    const handleAddNodeToChat = () => {
      setChatCollapsed(false)
    }
    window.addEventListener('add-node-to-chat', handleAddNodeToChat)
    return () => window.removeEventListener('add-node-to-chat', handleAddNodeToChat)
  }, [])

  useEffect(() => {
    const handleOpenAgentChat = () => {
      setChatCollapsed(false)
    }
    window.addEventListener('agent-chat:open', handleOpenAgentChat)
    return () => window.removeEventListener('agent-chat:open', handleOpenAgentChat)
  }, [])

  // Persist collapsed state to localStorage
  useEffect(() => {
    try { localStorage.setItem('chat-sidebar-collapsed', String(chatCollapsed)) } catch { /* ignore */ }
  }, [chatCollapsed])

  return (
    <>
    <WorkspaceSidebar
      collapsed={effectiveChatCollapsed}
      width={chatWidth}
      mobileMode={mobileMode}
      className="!bg-chrome/[0.04]"
    >
      {/* Resize handle */}
      {!effectiveChatCollapsed && !mobileMode && (
        <ResizeHandle
          onPointerDown={handleResizePointerDown}
          testId="chat-sidebar-resize-handle"
          className="touch-none"
        />
      )}

      {/* Sidebar Header — bottom border + slightly deeper wash than the
          sidebar body, so the header still reads as a distinct section on top
          of the sidebar. Neutral `chrome` rather than the canvas amber. */}
      {/* Fixed height (not content-sized) so the bottom border lines up
          with the canvas top bar (`AppHeader`) beside it. */}
      <SidebarHeader
        className={`${WORKSPACE_TOP_BAR_HEIGHT_CLASS} flex flex-shrink-0 flex-col justify-center !py-0 !border-b-chrome/30 bg-chrome/[0.06]`}
      >
        {/* Keep the dock's portal dialogs mounted so the simplified Asset
            Library rail can open them while the chat is collapsed. */}
          <div className={effectiveChatCollapsed ? 'hidden' : 'flex flex-col gap-2'}>
            <div className="flex items-center justify-between gap-2">
              {/* Left group: back-chevron → Dreamwrite brandmark. */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link
                  to="/dashboard"
                  className="group text-chrome/70 hover:text-chrome transition-colors flex-shrink-0"
                  title={t('chatSidebar.backToDashboard')}
                >
                  <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                </Link>
                <div
                  className="flex items-center gap-2"
                  data-testid="chat-sidebar-dreamwrite-chip"
                >
                  {/* Crescent brandmark — PNG tinted via CSS mask so we don't
                      ship a second logo asset. Background paints through the
                      PNG's alpha channel, giving a pure `bg-chrome` glyph. */}
                  <span
                    aria-hidden
                    className="inline-block w-4 h-4 bg-chrome flex-shrink-0"
                    style={{
                      WebkitMaskImage: "url('/landing-v2/logo-icon.png')",
                      maskImage: "url('/landing-v2/logo-icon.png')",
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain',
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                    }}
                  />
                  <span className="font-[Fustat] text-lg font-semibold text-chrome leading-none tracking-tight">
                    {import.meta.env.DEV && import.meta.env.VITE_DIRECTOR_PROVIDER === 'claude-code'
                      ? t('chatSidebar.directorClaudeCode')
                      : t('chatSidebar.director')}
                  </span>
                </div>
                {import.meta.env.DEV && import.meta.env.VITE_DIRECTOR_PROVIDER === 'claude-code' && (
                  <div className="text-xxs font-medium text-chrome/70 flex items-center gap-1 bg-chrome/10 px-2 py-1 rounded" data-testid="claude-code-badge">
                    <Terminal className="w-3 h-3" />
                  </div>
                )}
              </div>

              {/* Right group: dock buttons ([Add Nodes] [Assets] [Kbd Shortcuts])
                  + mobile-canvas-jump / collapse. Dock leans right per Fia's
                  ask so the header reads brandmark ⇢ … ⇢ actions. */}
              <div className="flex items-center gap-1 min-w-0">
                {mobileMode ? (
                  <button
                    type="button"
                    onClick={onShowCanvas}
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 flex-shrink-0"
                    title={t('chatSidebar.showCanvas')}
                    data-testid="mobile-show-canvas"
                  >
                    <ChevronsRight className="w-4 h-4" />
                    {t('chatSidebar.canvas')}
                  </button>
                ) : (
                  <button
                    onClick={() => setChatCollapsed(!chatCollapsed)}
                    className="p-1.5 rounded-lg hover:bg-chrome/10 transition-colors text-chrome/70 hover:text-chrome cursor-pointer flex-shrink-0"
                    title={t('chatSidebar.collapseChat')}
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

        {/* Collapsed state header */}
        {effectiveChatCollapsed && (
          <button
            onClick={() => setChatCollapsed(false)}
            className="p-1.5 rounded-lg hover:bg-chrome/10 transition-colors text-chrome/70 hover:text-chrome mx-auto block cursor-pointer"
            title={t('chatSidebar.expandChat')}
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        )}
      </SidebarHeader>

      {/* Chat Content */}
      <SidebarContent>
        {!effectiveChatCollapsed && (
          <AgentChat
            key={`${projectId}:${canvasId}`}
            projectId={projectId}
            canvasId={canvasId}
            initialPrompt={initialPrompt}
            initialSelectedSkill={initialSelectedSkill}
            initialReferencedNodeIds={initialReferencedNodeIds}
            pendingActions={pendingActions}
            initialReplayScenario={initialReplayScenario}
            className="flex-1 overflow-hidden h-full"
            autoConfirmCostSettings={autoConfirmCostSettings}
            canvasNodes={canvasNodes}
            replayCanvasNodes={replayCanvasNodes}
            canvasEdges={canvasEdges}
            onReplayCreateCanvas={onReplayCreateCanvas}
            onReplayDeleteCanvas={onReplayDeleteCanvas}
            onCanvasCreated={onCanvasCreated}
            onChatSocketConnectedChange={onChatSocketConnectedChange}
            ydocRunningCount={ydocRunningCount}
            ydocDirectorRunning={ydocDirectorRunning}
            ydocStatusLive={ydocStatusLive}
            ydocQueuedCount={ydocQueuedCount}
            onMentionClick={onMentionClick}
            onUploadMedia={onUploadMedia}
            executionPlanBatches={executionPlanBatches}
            onConfirmBatch={onConfirmBatch}
            onCancelBatch={onCancelBatch}
          />
        )}
      </SidebarContent>

      {/* Collapsed state - show icon to expand */}
      {effectiveChatCollapsed && (
        <div className="flex-1 flex flex-col items-center pt-6 gap-4">
          <button
            onClick={() => setChatCollapsed(false)}
            className="p-2.5 rounded-md bg-chrome/10 hover:bg-chrome/20 border border-chrome/30 hover:border-chrome/50 transition-all cursor-pointer"
            title={t('chatSidebar.openDirectorChat')}
            aria-label={t('chatSidebar.director')}
          >
            {/* Crescent brandmark — same mask technique as the expanded chip
                so the collapsed icon tracks it rather than falling back to the
                PNG's native off-white. */}
            <span
              aria-hidden
              className="inline-block w-5 h-5 bg-chrome"
              style={{
                WebkitMaskImage: "url('/landing-v2/logo-icon.png')",
                maskImage: "url('/landing-v2/logo-icon.png')",
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
              }}
            />
          </button>
        </div>
      )}

    </WorkspaceSidebar>
    </>
  )
})
